import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import notification from "../../../../../helpers/notification";
import { TspBreadcrumb, TspCard, TspFormField } from "../../../components/primitives";
import useTspPermission from "../../../hooks/useTspPermission";
import { tspErrorMessage } from "../../../hooks/useTspApiResource";
import { navigateWithFlash } from "../../../hooks/useTspFlashNotice";
import TSP_PERMISSIONS from "../../../constants/tspPermissions";
import { ADVICE_SECTIONS, ADVICE_SECTION_MAX } from "../../../constants/tspAdvice";
import { ADVICE_FIELD_ORDER, buildCreateBody, buildUpdateBody, validateAdvice } from "../adviceModel";
import { createAdvice, updateAdvice, useTspJourneyCities } from "../useTspAdviceData";
import TspBackButton from "../../../components/common/TspBackButton";

// Shared Add / Edit form. `initialValues` seeds the state once (the parent only
// renders this after data is ready). A new advice names a journey that is in
// progress and one of that journey's cities; an existing one keeps both (they
// are fixed once written) and only its text changes. The server verifies the
// journey/city and decides who wrote it and which team it belongs to.
export default function AdviceForm({ mode, initialValues, item, journeys, presetLocationId }) {
  const navigate = useNavigate();
  const { hasPermission, loading: permissionsLoading } = useTspPermission();
  const canManage = hasPermission(TSP_PERMISSIONS.ADVICE_MANAGE);
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const presetApplied = useRef(false);

  const isEdit = mode === "edit";
  const backTo = isEdit ? `/tsp/advice/${item.id}` : "/tsp/advice";
  const isDirty = JSON.stringify(values) !== JSON.stringify(initialValues);
  const { cities, loading: citiesLoading } = useTspJourneyCities(isEdit ? null : values.journeyId);

  // A city preset from the URL (e.g. from a Knowledge city page) is applied once
  // the chosen journey's cities are known — and only if it is one of them.
  useEffect(() => {
    if (isEdit || presetApplied.current || !presetLocationId || cities.length === 0) return;
    presetApplied.current = true;
    if (cities.some((city) => String(city.locationId) === presetLocationId)) setValues((previous) => ({ ...previous, locationId: presetLocationId }));
  }, [cities, isEdit, presetLocationId]);

  const setField = (key, value) => {
    setValues((previous) => ({ ...previous, [key]: value, ...(key === "journeyId" ? { locationId: "" } : {}) })); // the city list depends on the journey
    setErrors((previous) => ({ ...previous, [key]: undefined, sections: undefined, ...(key === "journeyId" ? { locationId: undefined } : {}) }));
  };

  const invalid = (key) => (errors[key] ? "true" : undefined);

  const handleCancel = () => {
    if (isDirty && !window.confirm("Discard your changes?")) return;
    navigate(backTo);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    if (!canManage) {
      notification("error", "You do not have permission to save advice.");
      return;
    }
    const found = validateAdvice(values, { mode });
    setErrors(found);
    setServerError("");
    const firstInvalid = ADVICE_FIELD_ORDER.find((key) => found[key]);
    if (firstInvalid || found.sections) {
      notification("warning", "Please fix the highlighted fields.");
      document.getElementById(`tsp-advice-${firstInvalid || ADVICE_SECTIONS[0].key}`)?.focus();
      return;
    }

    setSubmitting(true);
    try {
      const record = isEdit ? await updateAdvice(item.id, buildUpdateBody(values)) : await createAdvice(buildCreateBody(values));
      navigateWithFlash(navigate, `/tsp/advice/${record.id}`, "success", isEdit ? "Advice updated." : "Advice added.");
    } catch (error) {
      setServerError(tspErrorMessage(error, "Could not save this advice. Please try again."));
      setSubmitting(false);
    }
  };

  const blocked = permissionsLoading || !canManage;

  return (
    <div className="tsp-page tsp-advice-page tsp-advice-form-page">
      <div className="tsp-page-heading-row">
        <div>
          <h1>{isEdit ? "Edit Advice" : "Add Advice"}</h1>
          <TspBreadcrumb
            items={[
              { label: "Dashboard", to: "/tsp" },
              { label: "Advice Repository", to: "/tsp/advice" },
              isEdit ? { label: `Advice for ${item.location?.label || "city"}`, to: `/tsp/advice/${item.id}` } : "Add Advice",
              ...(isEdit ? ["Edit"] : []),
            ]}
          />
        </div>
        <TspBackButton fallback={backTo} />
      </div>

      <form onSubmit={handleSubmit} noValidate aria-busy={submitting}>
        <TspCard title="Advice Details" subtitle="Share what the next team should know about this place." className="tsp-title-18">
          {serverError && (
            <p className="tsp-form-error" role="alert">
              {serverError}
            </p>
          )}

          <div className="tsp-journey-form-grid">
            {isEdit ? (
              <>
                <div className="tsp-form-field">
                  <span className="tsp-field-label">Journey</span>
                  <div className="tsp-field-value">{item.journey?.name || "—"}</div>
                </div>
                <div className="tsp-form-field">
                  <span className="tsp-field-label">City</span>
                  <div className="tsp-field-value">{item.location?.label || "—"}</div>
                </div>
              </>
            ) : (
              <>
                <TspFormField id="tsp-advice-journeyId" label="Journey" required error={errors.journeyId} hint={journeys.length === 0 ? "Advice can be added once a journey is in progress." : undefined}>
                  <select
                    id="tsp-advice-journeyId"
                    className="tsp-field-control"
                    value={values.journeyId}
                    aria-required="true"
                    aria-invalid={invalid("journeyId")}
                    disabled={submitting}
                    onChange={(event) => setField("journeyId", event.target.value)}
                  >
                    <option value="">Select journey</option>
                    {journeys.map((journey) => (
                      <option key={journey.id} value={journey.id}>
                        {journey.journey_name}
                      </option>
                    ))}
                  </select>
                </TspFormField>

                <TspFormField id="tsp-advice-locationId" label="City" required error={errors.locationId}>
                  <select
                    id="tsp-advice-locationId"
                    className="tsp-field-control"
                    value={values.locationId}
                    aria-required="true"
                    aria-invalid={invalid("locationId")}
                    disabled={submitting || !values.journeyId || citiesLoading}
                    onChange={(event) => setField("locationId", event.target.value)}
                  >
                    <option value="">{!values.journeyId ? "Select a journey first" : citiesLoading ? "Loading…" : "Select city"}</option>
                    {cities.map((city) => (
                      <option key={city.locationId} value={city.locationId}>
                        {city.label}
                      </option>
                    ))}
                  </select>
                </TspFormField>
              </>
            )}

            {ADVICE_SECTIONS.map((section) => (
              <TspFormField
                key={section.key}
                id={`tsp-advice-${section.key}`}
                label={section.label}
                error={errors[section.key]}
                hint={values[section.key] ? `${values[section.key].length}/${ADVICE_SECTION_MAX} characters` : section.hint}
                className="tsp-span-2"
              >
                <textarea
                  id={`tsp-advice-${section.key}`}
                  className="tsp-field-control tsp-textarea"
                  rows={4}
                  value={values[section.key]}
                  aria-invalid={invalid(section.key)}
                  disabled={submitting}
                  onChange={(event) => setField(section.key, event.target.value)}
                />
              </TspFormField>
            ))}
          </div>

          {errors.sections && (
            <p className="tsp-form-error" role="alert">
              {errors.sections}
            </p>
          )}

          <div className="tsp-form-actions">
            {!permissionsLoading && !canManage && (
              <p className="tsp-preview-note tsp-preview-note-warn" role="status">
                You do not have permission to save advice.
              </p>
            )}
            <button type="button" className="tsp-btn tsp-btn-outline" onClick={handleCancel} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="tsp-btn tsp-btn-success" disabled={blocked || submitting}>
              {submitting ? "Saving…" : isEdit ? "Save Changes" : "Save Advice"}
            </button>
          </div>
        </TspCard>
      </form>

      <ToastContainer />
    </div>
  );
}
